import { useCallback, useRef } from 'react';

import { useAppDispatch, store } from '@verantos-dev/app-state-base';
import {
  markConceptGroupSelectionsAsReported,
  selectConceptGroupSelectionsByFilterKey,
  selectConceptGroupSelectionsBySpan,
  selectConceptGroups,
  setConceptGroups,
} from '@verantos-dev/app-state-cohort-builder';
import type { AutoAcceptConceptGroupData } from '@verantos-dev/app-state-cohort-builder';
import {
  buildDefinitionFromStore,
  deriveConceptAttribute,
  getConceptRefKeys,
  resolveNamesToConceptRef,
  extractConceptNames,
  useCohortDefinitionStore,
} from '@verantos-dev/feature-analytical-variables';
import type { AnalyticalVariable, ConceptRef } from '@verantos-dev/types-cohorts';

import { useEmitResearchAssistantUpdate } from './useEmitResearchAssistantUpdate';

/**
 * Recursively walk a logic tree and collect ALL filter nodes whose `table` matches (case-insensitive).
 */
function findAllFiltersByTable(
  node: Record<string, unknown>,
  table: string
): Record<string, unknown>[] {
  const results: Record<string, unknown>[] = [];
  const nodeTable = typeof node.table === 'string' ? node.table.toLowerCase() : '';
  if (nodeTable === table.toLowerCase()) results.push(node);
  if (Array.isArray(node.filters)) {
    for (const child of node.filters) {
      results.push(...findAllFiltersByTable(child as Record<string, unknown>, table));
    }
  }
  if (Array.isArray(node.correlatedFilters)) {
    for (const corr of node.correlatedFilters) {
      const corrRecord = corr as Record<string, unknown>;
      const filter = corrRecord.filter as Record<string, unknown> | undefined;
      if (filter) {
        results.push(...findAllFiltersByTable(filter, table));
      }
    }
  }
  return results;
}

/**
 * Creates the auto-accept callback with access to feature-analytical-variables.
 * Used as the value for AutoAcceptConceptGroupContext.Provider in CohortVersionLayout.
 */
export const useAutoAcceptConceptGroupProvider = () => {
  const dispatch = useAppDispatch();
  const emitResearchAssistantUpdate = useEmitResearchAssistantUpdate();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Stable callback — reads concept groups from store at call time to avoid
  // recreating the callback on every Redux state change (which causes infinite re-renders).
  const autoAccept = useCallback(
    ({ conceptGroupId, conceptGroupName, filterKey }: AutoAcceptConceptGroupData) => {
      const reduxState = store.getState();
      const storeState = useCohortDefinitionStore.getState();
      const analyticalVariables = storeState.analyticalVariables as AnalyticalVariable[];
      const conceptGroups = selectConceptGroups(reduxState);

      // 1. Find the original selection by filterKey to get the original span
      const originSelection = selectConceptGroupSelectionsByFilterKey(filterKey)(reduxState);
      if (!originSelection) return;
      const originalSpan = originSelection.span;

      // 2. Find ALL selections matching the original span (across all AVs)
      const allSelections = selectConceptGroupSelectionsBySpan(originalSpan)(reduxState);
      if (!allSelections.length) return;

      // 3. Build name-to-ID maps (include the new concept group)
      const nameToIdMap = new Map<string, string>();
      const conceptGroupIdToNameMap = new Map<string, string>();
      conceptGroups?.forEach((cg: { conceptGroupId: string; conceptGroupName: string }) => {
        nameToIdMap.set(cg.conceptGroupName.toLowerCase(), cg.conceptGroupId);
        conceptGroupIdToNameMap.set(cg.conceptGroupId, cg.conceptGroupName);
      });
      nameToIdMap.set(conceptGroupName.toLowerCase(), conceptGroupId);
      conceptGroupIdToNameMap.set(conceptGroupId, conceptGroupName);

      const updatedAvIds: string[] = [];
      const reportedFilterKeys: string[] = [];

      // 4. Group selections by containerName → AV
      const selectionsByContainer = new Map<string, typeof allSelections>();
      for (const sel of allSelections) {
        if (!sel.containerName) continue;
        const key = sel.containerName.toLowerCase();
        const list = selectionsByContainer.get(key) ?? [];
        list.push(sel);
        selectionsByContainer.set(key, list);
      }

      // 5. For each AV, update ALL matching conceptRef fields
      for (const [containerKey, selections] of selectionsByContainer) {
        const av = analyticalVariables.find((a) => a.name?.toLowerCase() === containerKey);
        if (!av?.logic) continue;

        const logicClone = JSON.parse(JSON.stringify(av.logic)) as Record<string, unknown>;
        let updated = false;

        for (const selection of selections) {
          if (!selection.table || !selection.attribute) continue;

          // Normalize table to lowercase (selections use UPPERCASE, AV logic uses lowercase)
          const normalizedTable = selection.table.toLowerCase();
          const targetFilters = findAllFiltersByTable(logicClone, normalizedTable);

          for (const targetFilter of targetFilters) {
            const refKeys = getConceptRefKeys(targetFilter);
            const matchingKey = refKeys.find(
              (key) => deriveConceptAttribute(normalizedTable, key) === selection.attribute
            );
            if (!matchingKey || !targetFilter[matchingKey]) continue;

            const existingNames = extractConceptNames(
              targetFilter[matchingKey] as ConceptRef,
              conceptGroupIdToNameMap
            );

            // Replace the original span name with the new concept group name
            const updatedNames = existingNames.map((name) =>
              name.toLowerCase() === originalSpan.toLowerCase() ? conceptGroupName : name
            );
            if (!updatedNames.some((n) => n.toLowerCase() === conceptGroupName.toLowerCase())) {
              updatedNames.push(conceptGroupName);
            }

            targetFilter[matchingKey] = resolveNamesToConceptRef(
              targetFilter[matchingKey] as object,
              updatedNames,
              nameToIdMap
            );
            updated = true;
          }

          reportedFilterKeys.push(selection.filterKey);
        }

        if (updated) {
          storeState.updateAnalyticalVariable(av.id, {
            logic: logicClone as unknown as AnalyticalVariable['logic'],
          });
          storeState.addRecentlyAppliedConceptGroup(av.id, conceptGroupName);
          updatedAvIds.push(av.id);
        }
      }

      // 6. Mark all matched selections as reported
      if (reportedFilterKeys.length > 0) {
        dispatch(markConceptGroupSelectionsAsReported({ filterKeys: reportedFilterKeys }));
      }

      // 7. Queue ALL affected AVs for preview modal review
      if (updatedAvIds.length > 0) {
        storeState.setPendingPreviewAvIds(updatedAvIds);
      }

      // 8. Optimistically ensure the new concept group is in Redux so chips
      //    render as available immediately (before any RTK Query refetch lands).
      const currentConceptGroups = selectConceptGroups(store.getState());
      const alreadyPresent = currentConceptGroups?.some(
        (cg: { conceptGroupId: string }) => cg.conceptGroupId === conceptGroupId
      );
      if (!alreadyPresent && updatedAvIds.length > 0) {
        dispatch(
          setConceptGroups([...(currentConceptGroups ?? []), { conceptGroupId, conceptGroupName }])
        );
      }

      // 9. Persist changes to the backend via the socket save pipeline
      if (updatedAvIds.length > 0) {
        emitResearchAssistantUpdate(buildDefinitionFromStore());
      }

      // 10. Auto-expire the highlights after 15 seconds
      clearTimeout(timeoutRef.current);
      const avIdsToExpire = [...updatedAvIds];
      timeoutRef.current = setTimeout(() => {
        const currentState = useCohortDefinitionStore.getState();
        for (const avId of avIdsToExpire) {
          currentState.clearRecentlyAppliedConceptGroup(avId, conceptGroupName);
        }
      }, 15_000);
    },
    [dispatch, emitResearchAssistantUpdate]
  );

  return autoAccept;
};
